import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, { Controls, Background, BackgroundVariant, ReactFlowProvider, useReactFlow } from 'reactflow';
import 'reactflow/dist/style.css';
import { KNOWN_DEVICES, EDGE_AGENT_IP, EDGE_AGENT_NAME, PROTOCOL_COLORS, fmtUtc, plainPolicyName } from '../../constants/topology';
import { TOPOLOGY_NODE_TYPES } from './TopologyNodes';

const SPEED_DELAYS = { '0.5x': 3000, '1x': 1500, '2x': 750, '4x': 300 };

const WINDOW_OPTIONS = [
  { label: '15m', value: 0.25 },
  { label: '1h',  value: 1 },
  { label: '2h',  value: 2 },
  { label: '6h',  value: 6 },
  { label: '24h', value: 24 },
];

// ─── helpers ────────────────────────────────────────────────────────────────

// Normalize protocol strings from the agent (IEC61850_GOOSE → goose, etc.)
function normalizeProtocol(proto) {
  if (!proto) return 'tcp';
  const p = proto.toLowerCase();
  if (p === 'iec61850_goose') return 'goose';
  if (p === 'iec61850_mms')   return 'mms';
  if (p === 'iec61850')       return 'mms';
  return p;
}

function nodeName(ip, assetsByIp) {
  if (!ip) return null;
  // KNOWN_DEVICES (curated in topology.js) wins over stale DB asset names
  return KNOWN_DEVICES[ip]?.name || (assetsByIp && assetsByIp[ip]?.asset_name) || ip;
}

function stepLabel(s, assetsByIp) {
  const proto = normalizeProtocol(s.protocol);
  if (!s.sourceIp && !s.destIp && proto === 'goose') {
    return 'GOOSE · L2 Multicast Domain';
  }
  const src = nodeName(s.sourceIp, assetsByIp)
    || (s.isPeriodicPoll ? '[Polling]' : '[Internal Host]');
  const dst = nodeName(s.destIp, assetsByIp)
    || (s.destIp ? s.destIp : '[No Dest]');
  return `${src} → ${dst}`;
}

// ─── periodic poll detection ─────────────────────────────────────────────────
// Marks steps as isPeriodicPoll when: source_ip is null and they arrive at
// regular 2–10 min intervals to the same dest+protocol pair.
// Groups by destIp::protocol so Modbus, DNP3, MMS, and raw TCP are all caught.
// The agent DPI layer emits 'modbus' not 'tcp' for Modbus-over-TCP frames,
// which is why the old '=== tcp' guard missed the entire polling pattern.
function markPeriodicPolls(steps) {
  if (steps.length < 3) return steps;

  const byDestProto = {};
  steps.forEach((s, i) => {
    if (!s.sourceIp && s.destIp) {
      const key = `${s.destIp}::${normalizeProtocol(s.protocol)}`;
      (byDestProto[key] = byDestProto[key] || []).push({ s, i });
    }
  });

  const periodicIdx = new Set();

  Object.values(byDestProto).forEach(group => {
    if (group.length < 3) return;
    const intervals = [];
    for (let k = 1; k < group.length; k++) {
      intervals.push(
        (new Date(group[k].s.timestamp) - new Date(group[k - 1].s.timestamp)) / 1000
      );
    }
    const inRange = intervals.filter(iv => iv >= 120 && iv <= 600);
    if (inRange.length >= Math.ceil(intervals.length * 0.6)) {
      group.forEach(g => periodicIdx.add(g.i));
    }
  });

  return steps.map((s, i) => ({ ...s, isPeriodicPoll: periodicIdx.has(i) }));
}

// ─── event → steps ──────────────────────────────────────────────────────────
function buildAttackSteps(events) {
  if (!events || events.length === 0) return [];
  const sorted = [...events].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const raw = [];
  let cluster = null;

  sorted.forEach(ev => {
    const t = new Date(ev.timestamp).getTime();
    // An event carries dangerous OT intent if flagged as a write/dangerous action
    // in its details — used to escalate cluster severity regardless of cloud severity label.
    const isDangerous = !!(ev.details?.dnp3_is_dangerous_write || ev.details?.dnp3_is_dangerous
      || ev.details?.mms_is_write || ev.details?.bacnet_is_write);

    if (cluster && ev.source_ip === cluster.sourceIp && t - cluster.lastT <= 10000) {
      cluster.events.push(ev);
      cluster.lastT = t;
      if ((ev.severity || '').toLowerCase() === 'critical' || isDangerous) cluster.hasCritical = true;
      if (ev.enforcement_event) cluster.hasEnforcement = true;
      if (ev.policy_name && !cluster.policies.includes(ev.policy_name)) cluster.policies.push(ev.policy_name);
    } else {
      if (cluster) raw.push(cluster);
      cluster = {
        sourceIp: ev.source_ip,
        destIp:   ev.destination_ip || ev.dest_ip,
        protocol: ev.protocol,
        timestamp: ev.timestamp,
        lastT: t,
        events: [ev],
        hasCritical:    (ev.severity || '').toLowerCase() === 'critical' || isDangerous,
        hasEnforcement: !!ev.enforcement_event,
        policies: ev.policy_name ? [ev.policy_name] : [],
      };
    }
  });
  if (cluster) raw.push(cluster);

  // Drop steps with no resolved endpoints and no security signal.
  // Protocol-agnostic: catches TCP, HTTPS, null-protocol agent traffic alike.
  // A step is noise if: no source, no dest, no policy match, not critical, no enforcement.
  const signal = raw.filter(s => {
    if (s.sourceIp || s.destIp) return true;       // at least one endpoint known — keep
    if (s.hasCritical || s.hasEnforcement) return true;
    if (s.policies.length > 0) return true;
    return false;  // both endpoints unresolvable, no signal → drop
  });

  const numbered = signal.map((s, i) => ({ stepNum: i + 1, ...s }));

  // Mark the first step of each incident gap (> 15 min between consecutive steps)
  for (let i = 1; i < numbered.length; i++) {
    const gap = (new Date(numbered[i].timestamp) - new Date(numbered[i - 1].timestamp)) / 60000;
    if (gap > 15) numbered[i].incidentBreak = true;
  }

  return markPeriodicPolls(numbered);
}

// ─── semantic zone layout ────────────────────────────────────────────────────
// Fixed positions by IP so attack arrows span the canvas meaningfully.
// Four zones: substation (top-left), external threats (top-right),
// field/process (bottom-left), SCADA/HMI (bottom-right), edge agent (center).
const ZONE_POSITIONS = {
  // Substation protection zone — top-left
  '192.168.1.100': { x: 20,  y: 80  },
  '192.168.1.101': { x: 20,  y: 180 },
  '192.168.1.102': { x: 20,  y: 280 },
  '192.168.1.103': { x: 20,  y: 380 },
  '192.168.1.104': { x: 20,  y: 480 },
  '192.168.1.105': { x: 175, y: 480 },
  // Field / process zone — bottom-left
  '192.168.1.70':  { x: 120, y: 610 },   // Allen-Bradley PLC — primary target
  '192.168.1.71':  { x: 280, y: 610 },
  '192.168.1.75':  { x: 120, y: 710 },
  // SCADA / HMI zone — bottom-right
  '192.168.1.72':  { x: 660, y: 460 },
  '192.168.1.73':  { x: 660, y: 560 },
  '192.168.1.74':  { x: 660, y: 660 },
  // Edge agent — center
  [EDGE_AGENT_IP]: { x: 345, y: 330 },
};

// Unknown/external IPs stack in the top-right zone, two columns
function externalPosition(idx) {
  return { x: 520 + (idx % 2) * 165, y: 80 + Math.floor(idx / 2) * 115 };
}

// Fleet-mode grid layout: place assets in rows below the edge agent (x=345, y=330)
function fleetAssetPosition(allFleetIps, ip) {
  const COLS = Math.min(allFleetIps.length, 4);
  const COL_GAP = 160;
  const idx = allFleetIps.indexOf(ip);
  if (idx === -1) return externalPosition(0);
  const row = Math.floor(idx / COLS);
  const col = idx % COLS;
  const rowCount = Math.min(allFleetIps.length - row * COLS, COLS);
  const rowStartX = Math.max(0, 345 - ((rowCount - 1) * COL_GAP) / 2);
  return { x: rowStartX + col * COL_GAP, y: 490 + row * 170 };
}

const ZONE_LABELS = [
  { id: 'zlabel-sub',      label: 'SUBSTATION',       color: '#1e3a5f', x: 22,  y: 48  },
  { id: 'zlabel-ext',      label: 'EXTERNAL SOURCES', color: '#5f1e1e', x: 520, y: 48  },
  { id: 'zlabel-field',    label: 'FIELD / PROCESS',  color: '#1e3a1e', x: 22,  y: 578 },
  { id: 'zlabel-scada',    label: 'SCADA / HMI',      color: '#1e2a4a', x: 660, y: 428 },
];

// Declared non-OT infrastructure IPs — gateway, agent host, etc.
// These are legitimate RFC-1918 IPs that should never be labeled Rogue Internal.
const EXCLUDED_OT_IPS = new Set([
  '10.10.1.1', '10.20.1.1', '10.30.1.1',  // substation site gateways
  '192.168.1.1',                            // home/lab LAN gateway
]);

// OT address scope: 192.168.1.60-120 for Pi/local-lab; any 10.x.x.x for fleet sites.
function isInOtScope(ip, assetsByIp) {
  if (assetsByIp && assetsByIp[ip]) return true;  // registered asset = always in scope
  if (ip.startsWith('10.')) return true;           // fleet subnets are OT networks
  if (!ip.startsWith('192.168.1.')) return false;
  const last = parseInt(ip.split('.').pop(), 10);
  return last >= 60 && last <= 120;
}

// Infer a role label for IPs not in KNOWN_DEVICES.
// Three states for LAN IPs: Rogue Internal (OT scope + violation), Uninventoried OT (OT scope, no violation),
// Out-of-Scope (outside OT range — gateway, PCs, etc. — never labeled Rogue just for being unknown).
function inferExternalRole(ip, steps, assetsByIp) {
  // Declared infra IPs (gateways etc.) — Out-of-Scope before any other check
  if (EXCLUDED_OT_IPS.has(ip)) return 'Out-of-Scope';

  const isLan = ip.startsWith('192.168.') || ip.startsWith('10.') ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(ip);

  if (!isLan) {
    const asSource = steps.some(s => s.sourceIp === ip);
    const asDest   = steps.some(s => s.destIp   === ip);
    const hasBlock = steps.some(s => s.sourceIp === ip && s.hasEnforcement);
    if (hasBlock) return 'Candidate Origin';
    if (asSource && !asDest) return 'Scanner';
    return 'External Host';
  }

  const hasViolation = steps.some(s =>
    (s.sourceIp === ip || s.destIp === ip) && (s.hasCritical || s.hasEnforcement)
  );
  if (isInOtScope(ip, assetsByIp)) {
    return hasViolation ? 'Rogue Internal' : 'Uninventoried OT';
  }
  return 'Out-of-Scope';
}

// ─── steps → ReactFlow ───────────────────────────────────────────────────────
function buildAttackGraph(steps, currentStep, edgeNode, assetsByIp) {
  const nodes = [];
  const edges = [];

  const activeStep = steps?.[currentStep] || null;

  // Collect IPs involved up to current step
  const involvedIps = new Set();
  (steps || []).slice(0, currentStep + 1).forEach(s => {
    if (s.sourceIp) involvedIps.add(s.sourceIp);
    if (s.destIp)   involvedIps.add(s.destIp);
  });
  involvedIps.add(EDGE_AGENT_IP);

  // Separate involved known-zone IPs from external/unknown
  const ipList = Array.from(involvedIps);
  const externalIps = ipList.filter(ip => ip !== EDGE_AGENT_IP && !ZONE_POSITIONS[ip]);
  let extIdx = 0;

  const hasFleetAssets = assetsByIp && Object.keys(assetsByIp).length > 0;
  const allFleetIps = hasFleetAssets ? Object.keys(assetsByIp) : [];

  // Zone label nodes — skip in fleet mode (labels are Pi/local-lab specific)
  if (!hasFleetAssets) {
    ZONE_LABELS.forEach(z => {
      nodes.push({
        id: z.id, type: 'zoneLabel',
        position: { x: z.x, y: z.y },
        data: { label: z.label, color: z.color },
        selectable: false, draggable: false,
      });
    });
  }

  // Render dimmed background nodes to fill zones.
  // Fleet sites use assets table; Pi/local-lab falls back to KNOWN_DEVICES.
  if (hasFleetAssets) {
    // Fleet mode: render registered assets that aren't yet involved as dimmed background
    Object.values(assetsByIp).forEach(asset => {
      const ip = asset.ip_address;
      if (involvedIps.has(ip)) return;
      const pos = ZONE_POSITIONS[ip] || fleetAssetPosition(allFleetIps, ip);
      nodes.push({
        id: `node-${ip}`, type: 'otDevice', position: pos,
        data: {
          ip,
          name: asset.asset_name || ip,
          role: asset.asset_type || 'Device',
          riskLevel: 'normal', protocols: [], isInactive: true,
        },
      });
    });
  } else {
    // Pi/local-lab mode: render all 12 KNOWN_DEVICES dimmed
    Object.entries(KNOWN_DEVICES).forEach(([ip, kd]) => {
      if (involvedIps.has(ip)) return;
      const pos = ZONE_POSITIONS[ip];
      if (!pos) return;
      nodes.push({
        id: `node-${ip}`, type: 'otDevice', position: pos,
        data: { ip, name: kd.name, role: kd.role, riskLevel: 'normal', protocols: [], isInactive: true },
      });
    });
  }

  // Active / involved device nodes
  ipList.forEach(ip => {
    const pos = ZONE_POSITIONS[ip] ||
      (hasFleetAssets && allFleetIps.includes(ip)
        ? fleetAssetPosition(allFleetIps, ip)
        : externalPosition(extIdx++));
    if (ip === EDGE_AGENT_IP) {
      nodes.push({
        id: `node-${ip}`, type: 'edgeAgent', position: pos,
        data: {
          hostname: edgeNode?.edge_node_id || edgeNode?.hostname || EDGE_AGENT_NAME,
          policiesLoaded: edgeNode?.policies_loaded || 0,
          status: edgeNode?.status || 'Online',
        },
      });
    } else {
      const kd = KNOWN_DEVICES[ip];
      const liveAsset = assetsByIp && assetsByIp[ip];
      const isExternal = externalIps.includes(ip);
      const isCurrentTarget = activeStep?.destIp === ip;
      const extRole = isExternal ? inferExternalRole(ip, steps || [], assetsByIp) : null;
      const lastOctet = ip.split('.').slice(-1)[0];
      nodes.push({
        id: `node-${ip}`, type: 'otDevice', position: pos,
        data: {
          ip,
          name: liveAsset?.asset_name || kd?.name || (isExternal ? `${extRole} · .${lastOctet}` : ip),
          role: liveAsset?.asset_type || kd?.role || extRole || 'Unknown',
          riskLevel: isCurrentTarget
            ? (activeStep?.hasCritical ? 'critical' : 'high')
            : (extRole === 'Candidate Origin' || extRole === 'Rogue Internal') ? 'high' : 'normal',
          protocols: [],
          isInactive: extRole === 'Out-of-Scope',
        },
      });
    }
  });

  (steps || []).slice(0, currentStep + 1).forEach((step, idx) => {
    const srcId = `node-${step.sourceIp}`;
    const dstId = `node-${step.destIp}`;
    const isActive = idx === currentStep;
    const isPast   = idx < currentStep;
    const proto = normalizeProtocol(step.protocol);
    const color = PROTOCOL_COLORS[proto] || '#475569';

    if (!step.sourceIp || !step.destIp) return;
    if (!nodes.find(n => n.id === srcId) || !nodes.find(n => n.id === dstId)) return;

    edges.push({
      id: `step-${idx}`,
      source: srcId, target: dstId,
      animated: isActive,
      style: {
        stroke: isActive ? color : `${color}44`,
        strokeWidth: isActive ? 4 : 1.5,
        filter: isActive ? `drop-shadow(0 0 5px ${color})` : undefined,
        strokeDasharray: step.hasEnforcement ? '4 2' : undefined,
        opacity: isPast ? 0.35 : 1,
      },
      label: isActive
        ? `${proto.toUpperCase()}${step.policies[0] ? ` · ${step.policies[0]}` : ''}`
        : proto.toUpperCase(),
      labelStyle: { fill: isActive ? color : `${color}66`, fontSize: isActive ? 10 : 9, fontWeight: isActive ? 700 : 400 },
      labelBgStyle: { fill: isActive ? 'rgba(15,23,42,0.9)' : 'transparent' },
    });
  });

  return { nodes, edges };
}

// ─── component ───────────────────────────────────────────────────────────────
export default function TopologyAttackChain({ events, assets, edgeNode, windowHours = 2, setWindowHours, onRefresh }) {
  const allSteps    = useMemo(() => buildAttackSteps(events), [events]);
  const [hidePolling, setHidePolling] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying]   = useState(false);
  const [speed, setSpeed]           = useState('1x');
  const [selectedStepIdx, setSelectedStepIdx] = useState(null);
  const [aiSummary, setAiSummary]   = useState('');
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [showPolicies, setShowPolicies] = useState(false);
  const [showRawEvidence, setShowRawEvidence] = useState(null);
  const playRef    = useRef(null);
  const stepListRef = useRef(null);
  const rfInstanceRef = useRef(null);

  const displaySteps = useMemo(
    () => hidePolling ? allSteps.filter(s => !s.isPeriodicPoll) : allSteps,
    [allSteps, hidePolling]
  );

  const pollCount = useMemo(
    () => allSteps.filter(s => s.isPeriodicPoll).length,
    [allSteps]
  );

  const pollingGroups = useMemo(() => {
    if (!hidePolling || pollCount === 0) return [];
    const polls = allSteps.filter(s => s.isPeriodicPoll);
    const byKey = {};
    polls.forEach(s => {
      const key = `${s.destIp}::${normalizeProtocol(s.protocol)}`;
      if (!byKey[key]) byKey[key] = {
        dest: nodeName(s.destIp) || s.destIp || '—',
        proto: normalizeProtocol(s.protocol).toUpperCase(),
        steps: [],
      };
      byKey[key].steps.push(s);
    });
    return Object.values(byKey).map(g => {
      let avgMin = null;
      if (g.steps.length >= 2) {
        const ivs = [];
        for (let i = 1; i < g.steps.length; i++)
          ivs.push((new Date(g.steps[i].timestamp) - new Date(g.steps[i - 1].timestamp)) / 60000);
        avgMin = Math.round(ivs.reduce((a, b) => a + b, 0) / ivs.length);
      }
      return { dest: g.dest, proto: g.proto, count: g.steps.length, avgMin };
    });
  }, [allSteps, hidePolling, pollCount]);

  // Policy fire aggregations — sorted critical-first, then by count
  const policyStats = useMemo(() => {
    const map = {};
    displaySteps.forEach(step => {
      step.events.forEach(ev => {
        const key = ev.policy_name || ev.rule_name;
        if (!key) return;
        if (!map[key]) {
          map[key] = { name: key, severity: ev.severity || 'Unknown', count: 0, lastFire: ev.timestamp };
        }
        map[key].count++;
        if (ev.timestamp > map[key].lastFire) map[key].lastFire = ev.timestamp;
      });
    });
    const SEV = { critical: 0, high: 1, warning: 2, medium: 3, low: 4 };
    return Object.values(map).sort((a, b) => {
      const sa = SEV[(a.severity || '').toLowerCase()] ?? 5;
      const sb = SEV[(b.severity || '').toLowerCase()] ?? 5;
      return sa !== sb ? sa - sb : b.count - a.count;
    });
  }, [displaySteps]);

  // Clamp step index when filter changes
  useEffect(() => {
    setCurrentStep(prev => Math.min(prev, Math.max(0, displaySteps.length - 1)));
    setSelectedStepIdx(null);
  }, [displaySteps.length]);

  // Auto-fit viewport once when steps first arrive (data loaded after mount)
  const didFitRef = useRef(false);
  useEffect(() => {
    if (!didFitRef.current && displaySteps.length > 0 && rfInstanceRef.current) {
      didFitRef.current = true;
      setTimeout(() => rfInstanceRef.current?.fitView({ padding: 0.15, duration: 400 }), 60);
    }
  }, [displaySteps.length]);

  const assetsByIp = useMemo(() => {
    const map = {};
    (assets || []).forEach(a => { if (a.ip_address) map[a.ip_address] = a; });
    return map;
  }, [assets]);

  const { nodes: rfNodes, edges: rfEdges } = useMemo(
    () => buildAttackGraph(displaySteps, currentStep, edgeNode, assetsByIp),
    [displaySteps, currentStep, edgeNode, assetsByIp]
  );

  // Auto-scroll
  useEffect(() => {
    if (stepListRef.current) {
      const el = stepListRef.current.querySelector('[data-active="true"]');
      if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [currentStep]);

  // Play timer
  useEffect(() => {
    if (isPlaying) {
      playRef.current = setInterval(() => {
        setCurrentStep(prev => {
          if (prev >= displaySteps.length - 1) { setIsPlaying(false); return prev; }
          return prev + 1;
        });
      }, SPEED_DELAYS[speed]);
    }
    return () => clearInterval(playRef.current);
  }, [isPlaying, speed, displaySteps.length]);

  // Clear AI summary whenever the time window changes so it regenerates for the new window
  useEffect(() => {
    setAiSummary('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowHours]);

  // AI summary — exclude periodic polling events, send only real attack events
  useEffect(() => {
    if (events.length === 0) { setAiSummary(''); return; }
    if (aiSummary) return;
    setSummaryLoading(true);
    const apiBase = process.env.REACT_APP_API_URL || '';
    const attackEvents = allSteps.filter(s => !s.isPeriodicPoll).flatMap(s => s.events);
    const eventSummary = attackEvents.slice(0, 25).map(e =>
      `[${e.timestamp}] ${e.severity} ${normalizeProtocol(e.protocol).toUpperCase()} ${e.source_ip || 'Unknown'} → ${e.dest_ip || e.destination_ip || 'Unknown'}: ${e.summary}`
    ).join('\n');
    fetch(`${apiBase}/chat/`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: `Analyze this OT network attack chain. Respond in exactly 4 sections with uppercase headers:\n\nATTACK STAGES\nIdentify the phases (Discovery, Lateral Movement, Command Execution, Enforcement). One sentence per phase active in this chain.\n\nASSETS TARGETED\nNumber of source IPs, which target devices (use device names, not IPs), any pivot points.\n\nPOLICY RESPONSE\nHow many policies fired. What severity categories. Do NOT list policy names — count them by type.\n\nENFORCEMENT\nHow many enforcement actions, whether dry-run, what protocols were blocked.\n\nEvents:\n${eventSummary}`,
        history: [], session_id: `attack-chain-${Date.now()}`,
      }),
    })
      .then(res => res.body)
      .then(body => {
        const reader = body.getReader();
        const decoder = new TextDecoder();
        let text = '';
        const read = () => reader.read().then(({ done, value }) => {
          if (done) {
            if (!text) {
              setAiSummary(buildDeterministicSummary(allSteps.filter(s => !s.isPeriodicPoll), events));
            }
            setSummaryLoading(false);
            return;
          }
          decoder.decode(value, { stream: true }).split('\n').forEach(line => {
            if (line.startsWith('data: ')) {
              try { const d = JSON.parse(line.slice(6)); if (d.text) text += d.text; } catch {}
            }
          });
          setAiSummary(text);
          read();
        });
        read();
      })
      .catch(() => {
        setAiSummary(buildDeterministicSummary(allSteps.filter(s => !s.isPeriodicPoll), events));
        setSummaryLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  const displayStep = displaySteps[selectedStepIdx ?? currentStep];

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-1 min-h-0">

        {/* ── Left rail — step list ── */}
        <div className="w-64 flex-shrink-0 flex flex-col border-r border-slate-700/50 bg-slate-900/50" ref={stepListRef}>
          {/* Header + toggle */}
          <div className="p-3 border-b border-slate-700/50 space-y-2">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs font-semibold text-slate-300">Trace Steps</div>
              {onRefresh && (
                resetConfirm ? (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={async () => {
                        const base = process.env.REACT_APP_API_URL || '';
                        await fetch(`${base}/events/purge`, { method: 'DELETE', credentials: 'include' }).catch(() => {});
                        setResetConfirm(false); onRefresh();
                      }}
                      className="text-[8px] px-1.5 py-0.5 rounded bg-red-900/50 text-red-300 border border-red-700/50 hover:bg-red-800/60">
                      Clear all
                    </button>
                    <button
                      onClick={async () => {
                        const base = process.env.REACT_APP_API_URL || '';
                        await fetch(`${base}/events/purge?older_than_hours=1`, { method: 'DELETE', credentials: 'include' }).catch(() => {});
                        setResetConfirm(false); onRefresh();
                      }}
                      className="text-[8px] px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-400 border border-amber-700/40 hover:bg-amber-800/40">
                      {'> 1h old'}
                    </button>
                    <button onClick={() => setResetConfirm(false)}
                      className="text-[8px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-500 border border-slate-700 hover:text-slate-300">
                      ✕
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setResetConfirm(true)}
                    className="text-[9px] px-1.5 py-0.5 rounded border border-slate-700 text-slate-500 hover:text-red-400 hover:border-red-700/50 transition-colors">
                    Reset
                  </button>
                )
              )}
            </div>

            {/* Time window selector */}
            {setWindowHours && (
              <div className="flex items-center gap-0.5">
                {WINDOW_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setWindowHours(opt.value)}
                    className={`flex-1 py-0.5 rounded text-[9px] font-medium border transition-colors ${
                      windowHours === opt.value
                        ? 'text-cyan-400 bg-cyan-900/30 border-cyan-700/50'
                        : 'text-slate-600 bg-slate-800/60 border-slate-700 hover:text-slate-400'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            {/* Polling headline — the value proposition sentence */}
            {allSteps.length > 0 && (
              <div className="text-[10px] text-slate-500 leading-tight pb-1">
                <span className="text-slate-300 font-semibold">{allSteps.length}</span> total events
                {pollCount > 0 && (
                  <>
                    {' · '}
                    <span className="text-cyan-400 font-semibold">{displaySteps.length}</span> trace steps
                    {' · '}
                    <span className="text-amber-500 font-semibold">{pollCount}</span> routine polls {hidePolling ? 'filtered' : 'shown'}
                  </>
                )}
              </div>
            )}

            {pollCount > 0 && (
              <button
                onClick={() => setHidePolling(h => !h)}
                className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-[10px] font-medium transition-colors border ${
                  hidePolling
                    ? 'bg-amber-900/30 border-amber-700/50 text-amber-300'
                    : 'bg-slate-800/60 border-slate-700 text-slate-400 hover:text-slate-300'
                }`}
              >
                <div className="flex items-center gap-1.5">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                  </svg>
                  {hidePolling ? `${pollCount} polling steps hidden` : `Show all (incl. ${pollCount} polling)`}
                </div>
                <span className={`text-[8px] px-1.5 py-0.5 rounded ${hidePolling ? 'bg-amber-800/60' : 'bg-slate-700'}`}>
                  {hidePolling ? 'ON' : 'OFF'}
                </span>
              </button>
            )}
          </div>

          {/* Step list */}
          {displaySteps.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
              <div className="text-slate-600 text-xs mb-2">No events in time window</div>
              <div className="text-[10px] text-slate-700">Run tests to generate activity</div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              {hidePolling && pollingGroups.length > 0 && (
                <div className="px-3 py-2 border-b border-amber-900/30 bg-amber-950/20">
                  <div className="text-[9px] text-amber-600 font-medium mb-1">
                    {pollCount} routine polling step{pollCount !== 1 ? 's' : ''} suppressed
                  </div>
                  {pollingGroups.map((g, i) => (
                    <div key={i} className="text-[8px] text-slate-600 font-mono mt-0.5">
                      {g.count}× → {g.dest} · {g.proto}{g.avgMin ? ` · ~${g.avgMin}m` : ''}
                    </div>
                  ))}
                </div>
              )}
              {displaySteps.map((s, idx) => {
                const isActive   = idx === currentStep;
                const isPast     = idx < currentStep;
                const proto      = normalizeProtocol(s.protocol);
                const protoColor = PROTOCOL_COLORS[proto] || '#475569';
                const label      = stepLabel(s, assetsByIp);
                const isL2       = !s.sourceIp && !s.destIp && proto === 'goose';
                const isDefender = s.hasEnforcement;

                return (
                  <React.Fragment key={idx}>
                  {s.incidentBreak && (
                    <div className="px-3 py-1 border-b border-slate-700/50 flex items-center gap-2">
                      <div className="flex-1 h-px bg-slate-700/50" />
                      <span className="text-[8px] text-slate-600 font-medium tracking-wider">NEW INCIDENT</span>
                      <div className="flex-1 h-px bg-slate-700/50" />
                    </div>
                  )}
                  <button
                    key={idx}
                    data-active={isActive}
                    onClick={() => { setSelectedStepIdx(idx); setCurrentStep(idx); }}
                    className={`w-full text-left py-2 border-b border-slate-800 transition-colors
                      ${isDefender
                        ? 'pl-6 pr-3 border-l-0 border-r-2 border-r-cyan-500/70 bg-cyan-950/20 text-right'
                        : 'pl-3 pr-3 border-l-2 border-l-red-700/30'}
                      ${isActive
                        ? (isDefender ? 'bg-cyan-900/35 !border-r-cyan-400' : 'bg-cyan-900/25 !border-l-cyan-500')
                        : ''}
                      ${isPast && !isActive ? 'opacity-40 hover:opacity-70' : 'hover:bg-slate-800/40'}
                    `}
                  >
                    <div className="flex items-center justify-between mb-0.5">
                      {isDefender ? (
                        <span className="text-[9px] font-bold text-cyan-400">🛡 RESPONSE</span>
                      ) : (
                        <span className="text-[9px] font-bold text-slate-500">STEP {idx + 1}</span>
                      )}
                      <div className="flex items-center gap-1">
                        {s.hasCritical && <div className="w-1.5 h-1.5 rounded-full bg-red-500" />}
                        <span className="text-[8px] font-semibold px-1 rounded"
                          style={{ background: `${protoColor}22`, color: protoColor }}>
                          {proto.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className={`text-[10px] leading-tight ${
                      isL2 ? 'text-yellow-400/80 italic' :
                      isDefender ? 'text-cyan-300/80' : 'text-slate-400'
                    } font-mono`}>
                      {label}
                    </div>
                    <div className="text-[9px] text-slate-600 mt-0.5">
                      {fmtUtc(s.timestamp)}
                    </div>
                  </button>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Center — attack graph ── */}
        <div className="flex-1 relative" style={{ background: '#020617' }}>
          <ReactFlow
            nodes={rfNodes}
            edges={rfEdges}
            nodeTypes={TOPOLOGY_NODE_TYPES}
            onInit={(instance) => { rfInstanceRef.current = instance; }}
            minZoom={0.25}
            maxZoom={2}
            proOptions={{ hideAttribution: true }}
          >
            <Controls className="!bg-slate-800 !border-slate-700" />
            <Background color="#1E293B" variant={BackgroundVariant.Dots} gap={20} size={1} />
          </ReactFlow>

          {/* Bottom overlay removed — step detail is in right rail */}

          {displaySteps.length === 0 && allSteps.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-slate-700 text-sm">No event trace data for this window</div>
            </div>
          )}

          {displaySteps.length === 0 && allSteps.length > 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-slate-600 text-sm mb-2">All {allSteps.length} steps are periodic polling</div>
                <button onClick={() => setHidePolling(false)}
                  className="text-[11px] text-amber-400 underline">Show polling steps</button>
              </div>
            </div>
          )}
        </div>

        {/* ── Right rail ── */}
        <div className="w-72 flex-shrink-0 flex flex-col border-l border-slate-700/50 bg-slate-900/50 overflow-y-auto">

          {/* Active step — prominent top section */}
          <div className="p-4 border-b border-slate-700/50 flex-shrink-0">
            {displayStep ? (() => {
              const proto = normalizeProtocol(displayStep.protocol);
              const protoColor = PROTOCOL_COLORS[proto] || '#475569';
              return (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold text-slate-500 tracking-widest">
                      STEP {displayStep.stepNum} / {displaySteps.length}
                    </span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold"
                      style={{ background: `${protoColor}22`, color: protoColor, border: `1px solid ${protoColor}44` }}>
                      {proto.toUpperCase()}
                    </span>
                  </div>
                  <div className="text-[13px] font-semibold text-slate-100 leading-snug mb-1.5">
                    {nodeName(displayStep.sourceIp)
                      || (displayStep.isPeriodicPoll ? '[Polling]' : '[Internal Host]')}
                    <span className="text-slate-500 mx-1">→</span>
                    {nodeName(displayStep.destIp)
                      || displayStep.destIp || '[No Dest]'}
                  </div>
                  <div className="text-[10px] text-slate-500 mb-2.5">
                    {displayStep.events.length} event{displayStep.events.length !== 1 ? 's' : ''}
                    {' · '}
                    {fmtUtc(displayStep.timestamp)}
                    {displayStep.sourceIp && (
                      <span className="ml-1.5 font-mono text-slate-600">{displayStep.sourceIp}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 items-center">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded border font-semibold ${
                      displayStep.hasCritical
                        ? 'bg-red-900/40 text-red-400 border-red-700/40'
                        : 'bg-amber-900/30 text-amber-400 border-amber-700/40'
                    }`}>
                      {displayStep.hasCritical ? 'Critical' : 'Medium'}
                    </span>
                    {displayStep.hasEnforcement && (() => {
                      const tier = displayStep.events[0]?.details?.autonomy_tier;
                      const tierLabel = displayStep.events[0]?.details?.tier_label;
                      return (
                        <span className="text-[9px] px-1.5 py-0.5 rounded border bg-cyan-900/30 text-cyan-300 border-cyan-700/40 font-semibold"
                          title={tier || 'Enforcement action'}>
                          🛡 {tierLabel || 'EVALUATION'}{tier ? ` · ${tier}` : ''}
                        </span>
                      );
                    })()}
                    {displayStep.policies.slice(0, 2).map(p => (
                      <span key={p} className="text-[8px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700 truncate max-w-[140px]"
                        title={p}>{plainPolicyName(p)}</span>
                    ))}
                    {displayStep.policies.length > 2 && (
                      <span className="text-[8px] px-1 py-0.5 rounded bg-slate-800 text-slate-600 border border-slate-700/50">
                        +{displayStep.policies.length - 2} more
                      </span>
                    )}
                  </div>

                  {/* Action staged + policy ID */}
                  {(() => {
                    const ev0 = displayStep.events[0];
                    const action = ev0?.action || ev0?.details?.rule_action;
                    const policyId = ev0?.policy_id || ev0?.details?.policy_id;
                    return (action || policyId) ? (
                      <div className="mt-2 pt-2 border-t border-slate-800 space-y-0.5">
                        {action && (
                          <div className="text-[9px] text-slate-600">
                            Action: <span className={`font-semibold ${
                              action === 'block' || action === 'drop' ? 'text-red-400' :
                              action === 'alert' ? 'text-amber-400' : 'text-slate-400'
                            }`}>{action}</span>
                          </div>
                        )}
                        {policyId && (
                          <div className="flex items-center gap-1.5 text-[9px] text-slate-500 font-mono">
                            <span className="truncate max-w-[140px]" title={policyId}>
                              {policyId.length > 16 ? `${policyId.slice(0, 8)}…${policyId.slice(-6)}` : policyId}
                            </span>
                            <button
                              onClick={() => navigator.clipboard.writeText(policyId)}
                              title="Copy policy ID"
                              className="flex-shrink-0 text-slate-600 hover:text-cyan-400 transition-colors"
                            >
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    ) : null;
                  })()}

                  {/* Evidence expander */}
                  <div className="mt-2">
                    <button
                      className="text-[9px] text-cyan-700 hover:text-cyan-500 transition-colors"
                      onClick={() => setShowRawEvidence(prev =>
                        prev === displayStep.stepNum ? null : displayStep.stepNum
                      )}
                    >
                      {showRawEvidence === displayStep.stepNum ? '▲ hide evidence' : '▼ show evidence'}
                    </button>
                    {showRawEvidence === displayStep.stepNum && (
                      <pre className="mt-1 text-[8px] text-slate-500 font-mono leading-tight bg-slate-900/60 rounded p-1.5 overflow-auto max-h-32 border border-slate-700/40">
                        {JSON.stringify(displayStep.events[0]?.details || {}, null, 2)}
                      </pre>
                    )}
                  </div>
                </>
              );
            })() : (
              <div className="text-center py-3">
                <div className="text-[10px] text-slate-600">— no step selected —</div>
                <div className="text-[9px] text-slate-700 mt-1">Select a step or press play</div>
              </div>
            )}
          </div>

          {/* AI Analysis */}
          <div className="p-4 flex-1 overflow-y-auto">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
              <span className="text-xs font-semibold text-slate-300">Operator Summary</span>
              {hidePolling && pollCount > 0 && (
                <span className="text-[9px] text-amber-500 ml-auto">{pollCount} polls excluded</span>
              )}
            </div>
            {summaryLoading ? (
              <div className="space-y-1.5">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-3 rounded bg-slate-800 animate-pulse" style={{ width: `${55 + i * 10}%` }} />
                ))}
              </div>
            ) : aiSummary ? (
              <div className="text-[11px] text-slate-300 leading-relaxed whitespace-pre-wrap font-mono">{aiSummary}</div>
            ) : (
              <div className="text-[11px] text-slate-600">No trace events to analyze.</div>
            )}

            {/* Expandable policy fire log */}
            {policyStats.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-700/50">
                <button
                  onClick={() => setShowPolicies(p => !p)}
                  className="w-full flex items-center justify-between text-[10px] text-slate-500 hover:text-slate-300 transition-colors"
                >
                  <span>
                    {policyStats.reduce((s, p) => s + p.count, 0)} policy fires ·{' '}
                    <span className="text-slate-400">{policyStats.length} unique rule{policyStats.length !== 1 ? 's' : ''}</span>
                  </span>
                  <span className="text-slate-600 text-[8px]">{showPolicies ? '▲ hide' : '▼ expand'}</span>
                </button>
                {showPolicies && (
                  <div className="mt-2 space-y-1">
                    {policyStats.map((p, i) => (
                      <div key={i} className="text-[9px] rounded px-2 py-1.5 bg-slate-800/50 border border-slate-700/40">
                        <div className="flex items-start justify-between gap-1 mb-0.5">
                          <span className="text-slate-300 break-all leading-tight" title={p.name}>{plainPolicyName(p.name)}</span>
                          <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                            <span className={`px-1 py-0.5 rounded text-[7px] font-bold uppercase ${
                              (p.severity || '').toLowerCase() === 'critical' ? 'bg-red-900/60 text-red-400' :
                              (p.severity || '').toLowerCase() === 'high'     ? 'bg-orange-900/40 text-orange-400' :
                              'bg-amber-900/30 text-amber-500'
                            }`}>
                              {(p.severity || 'med').slice(0, 4)}
                            </span>
                            <span className="text-slate-500 font-mono">×{p.count}</span>
                          </div>
                        </div>
                        <div className="text-slate-700 font-mono text-[8px]">
                          last: {fmtUtc(p.lastFire)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Timeline controls ── */}
      <div className="flex-shrink-0 border-t border-slate-700/50 bg-slate-900/80 px-4 py-2 flex items-center gap-4">
        <div className="flex items-center gap-1">
          <button onClick={() => setCurrentStep(0)} disabled={displaySteps.length === 0}
            className="p-1 rounded text-slate-400 hover:text-slate-200 disabled:opacity-30">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M6 6h2v12H6zm3.5 6L20 18V6z" /></svg>
          </button>
          <button onClick={() => setCurrentStep(s => Math.max(0, s - 1))} disabled={displaySteps.length === 0}
            className="p-1 rounded text-slate-400 hover:text-slate-200 disabled:opacity-30">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M6 6h2v12H6zm3.5 6L16 16V8z" /></svg>
          </button>
          <button onClick={() => setIsPlaying(p => !p)} disabled={displaySteps.length === 0}
            className={`p-1.5 rounded-full transition-colors disabled:opacity-30 ${isPlaying ? 'bg-red-600/30 text-red-400' : 'bg-cyan-600/20 text-cyan-400'}`}>
            {isPlaying
              ? <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg>
              : <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>}
          </button>
          <button onClick={() => setCurrentStep(s => Math.min(displaySteps.length - 1, s + 1))} disabled={displaySteps.length === 0}
            className="p-1 rounded text-slate-400 hover:text-slate-200 disabled:opacity-30">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M6 18l8.5-6L6 6v12zm2-8.14L11.03 12 8 14.14V9.86zM16 6h2v12h-2z" /></svg>
          </button>
        </div>

        <div className="flex flex-col flex-1 gap-0.5">
          <div className="text-[9px] text-slate-600 uppercase tracking-wider leading-none">
            Replay event trace · 4× speed
          </div>
          <input
            type="range"
            min={0} max={Math.max(displaySteps.length - 1, 0)} value={currentStep}
            onChange={e => { setCurrentStep(Number(e.target.value)); setIsPlaying(false); }}
            disabled={displaySteps.length === 0}
            className="accent-cyan-500"
          />
        </div>

        <div className="text-[10px] text-slate-500 font-mono w-24 text-right">
          {displaySteps.length > 0
            ? `${currentStep + 1}/${displaySteps.length} · ${allSteps.length} total`
            : '—'}
        </div>

        <div className="flex items-center gap-1">
          {Object.keys(SPEED_DELAYS).map(s => (
            <button key={s} onClick={() => setSpeed(s)}
              className={`px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors ${speed === s ? 'bg-cyan-700/40 text-cyan-300' : 'text-slate-500 hover:text-slate-300'}`}>
              {s}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── MITRE ATT&CK for ICS phase classifier ───────────────────────────────────
// Priority: Inhibit > Impair > C2 > Lateral > Collection > Discovery
// SNMP and BACnet are classified by OPERATION, not just protocol:
//   WhoIs / scan / broadcast → Discovery
//   ReadProperty / GET against known device → Collection
//   WriteProperty / SET → Impair Process Control
function classifyAttackPhase(step) {
  const proto    = normalizeProtocol(step.protocol || '');
  const policies = (step.policies || []).map(p => p.toLowerCase()).join(' ');
  const srcKnown = !!(step.sourceIp && KNOWN_DEVICES[step.sourceIp]);
  const dstKnown = !!(step.destIp   && KNOWN_DEVICES[step.destIp]);
  const isRfc1918 = ip => ip &&
    (ip.startsWith('192.168.') || ip.startsWith('10.') ||
     /^172\.(1[6-9]|2\d|3[01])\./.test(ip));
  const srcExternal   = !!(step.sourceIp && !KNOWN_DEVICES[step.sourceIp] && !isRfc1918(step.sourceIp));
  const srcLanUnknown = !!(step.sourceIp && !KNOWN_DEVICES[step.sourceIp] &&  isRfc1918(step.sourceIp));

  // 1. Inhibit Response Function — restart, firmware writes, unauthorized override
  if (policies.includes('restart') || policies.includes('firmware') ||
      policies.includes('cold') || policies.includes('warm_restart') ||
      policies.includes('cold_restart'))
    return 'Inhibit Response Function';

  // 2. Impair Process Control — write/control/set commands to field devices
  if (policies.includes('operate') || policies.includes('crob') ||
      policies.includes('direct') ||
      policies.includes('snmp_set') || policies.includes('snmp set') ||
      policies.includes('writeproperty') || policies.includes('write_property'))
    return 'Impair Process Control';
  if ((proto === 'dnp3' || proto === 'mms') && policies.includes('write'))
    return 'Impair Process Control';
  if (proto === 'bacnet' && (policies.includes('write') || policies.includes('writeproperty')))
    return 'Impair Process Control';

  // 3. Command & Control — external source communicating back to attacker infra
  if (srcExternal && (proto === 'https' || proto === 'http' || proto === 'tcp'))
    return 'Command & Control';

  // 4. Lateral Movement — unauthorized LAN→OT crossing, or unusual known↔known path
  if (srcLanUnknown && dstKnown) return 'Lateral Movement';
  if (srcKnown && dstKnown && (proto === 'tcp' || proto === 'ssh' || proto === 'http'))
    return 'Lateral Movement';

  // 5. Collection — reads against inventoried OT devices
  //    Excludes BACnet recon (WhoIs/broadcast) which stays Discovery
  const isBacnetRecon = proto === 'bacnet' &&
    (policies.includes('whois') || policies.includes('who_is') ||
     policies.includes('broadcast') || policies.includes('scan'));
  if (!isBacnetRecon && dstKnown &&
      (proto === 'snmp' || proto === 'bacnet' || proto === 'modbus' ||
       proto === 'dnp3' || proto === 'mms'))
    return 'Collection';

  // 6. Discovery — scanning, enumeration, first-contact, broadcast
  if (proto === 'arp' || proto === 'icmp' ||
      policies.includes('scan') || policies.includes('portscan') ||
      policies.includes('discovery') || policies.includes('whois') ||
      policies.includes('who_is') || policies.includes('broadcast') ||
      policies.includes('first_contact') || policies.includes('arp'))
    return 'Discovery';

  return 'Discovery';
}

const PHASE_ORDER = ['Discovery', 'Collection', 'Lateral Movement', 'Command & Control', 'Impair Process Control', 'Inhibit Response Function'];

function buildDeterministicSummary(attackSteps, events) {
  if (attackSteps.length === 0) return 'No trace events in the selected time window.';

  const protocols   = [...new Set(attackSteps.map(s => normalizeProtocol(s.protocol)).filter(Boolean))];
  const allPolicies = [...new Set(attackSteps.flatMap(s => s.policies))];
  const enforced    = attackSteps.filter(s => s.hasEnforcement).length;
  const critCount   = attackSteps.filter(s => s.hasCritical).length;
  const sources     = [...new Set(attackSteps.map(s => s.sourceIp).filter(Boolean))];
  const targets     = [...new Set(attackSteps.map(s => s.destIp).filter(Boolean))];
  const knownTargets = targets.map(ip => KNOWN_DEVICES[ip]?.name || ip);

  // Three-state source classification using module-level isInOtScope()
  const isRfc1918Ip = ip =>
    ip.startsWith('192.168.') || ip.startsWith('10.') ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(ip);
  const srcHasViolation = ip => attackSteps.some(s =>
    (s.sourceIp === ip || s.destIp === ip) && (s.hasCritical || s.hasEnforcement)
  );
  const knownDeviceSources   = sources.filter(ip => KNOWN_DEVICES[ip]);
  const externalSources      = sources.filter(ip => !KNOWN_DEVICES[ip] && !isRfc1918Ip(ip));
  const rogueInternalSources = sources.filter(ip =>
    !KNOWN_DEVICES[ip] && isRfc1918Ip(ip) && isInOtScope(ip) && srcHasViolation(ip)
  );
  const outOfScopeSources    = sources.filter(ip =>
    !KNOWN_DEVICES[ip] && isRfc1918Ip(ip) && (!isInOtScope(ip) || !srcHasViolation(ip))
  );

  // MITRE ATT&CK for ICS phase grouping
  const phaseGroups = {};
  attackSteps.forEach(s => {
    const p = classifyAttackPhase(s);
    if (!phaseGroups[p]) phaseGroups[p] = [];
    phaseGroups[p].push(s);
  });
  const phaseLines = PHASE_ORDER
    .filter(p => phaseGroups[p]?.length > 0)
    .map(p => {
      const steps = phaseGroups[p];
      const protos = [...new Set(steps.map(s => normalizeProtocol(s.protocol).toUpperCase()))].filter(Boolean).join('/');
      return `  ${p}: ${steps.length} step${steps.length !== 1 ? 's' : ''} · ${protos}`;
    });

  const targetStr = knownTargets.length > 0
    ? knownTargets.slice(0, 4).join(', ') + (knownTargets.length > 4 ? ` +${knownTargets.length - 4} more` : '')
    : 'Unknown';
  const sourceStr = [
    externalSources.length > 0 &&
      `${externalSources.length} external IP${externalSources.length !== 1 ? 's' : ''}`,
    rogueInternalSources.length > 0 &&
      `${rogueInternalSources.length} rogue internal`,
    outOfScopeSources.length > 0 &&
      `${outOfScopeSources.length} out-of-scope`,
    knownDeviceSources.length > 0 &&
      `${knownDeviceSources.length} known device${knownDeviceSources.length !== 1 ? 's' : ''}`,
  ].filter(Boolean).join(', ') || 'unknown';

  return [
    'TRACE STAGES',
    ...phaseLines,
    '',
    'ASSETS TARGETED',
    `  Sources: ${sourceStr}`,
    `  Targets: ${targetStr}`,
    '',
    'POLICY RESPONSE',
    `  ${allPolicies.length} polic${allPolicies.length !== 1 ? 'ies' : 'y'} triggered across ${protocols.map(p => p.toUpperCase()).join(', ')} traffic`,
    `  ${critCount} critical step${critCount !== 1 ? 's' : ''} detected`,
    '',
    'EVALUATION',
    enforced > 0
      ? `  ${enforced} action${enforced !== 1 ? 's' : ''} recorded — enforcement active`
      : '  Observes. Never acts.',
  ].join('\n');
}
